import { Show, createSignal, splitProps, type Component, type JSX } from 'solid-js';
import { cn } from '~/shared/lib/cn';
import { keepDigits } from '~/shared/ui/DigitsInput';
import { NumberInput, type NumberInputProps } from '~/shared/ui/NumberInput';

export interface TextFieldProps
  extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'placeholder' | 'inputmode' | 'inputMode'> {
  label: string;
  error?: string;
  trailing?: JSX.Element;
  inputmode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'search';
  digits?: boolean;
}

let uid = 0;

export const TextField: Component<TextFieldProps> = (props) => {
  const [local, rest] = splitProps(props, [
    'label',
    'error',
    'trailing',
    'class',
    'id',
    'value',
    'onInput',
    'onFocus',
    'onBlur',
    'digits',
    'inputmode',
  ]);

  const id = local.id ?? `tf-${++uid}`;
  const [focused, setFocused] = createSignal(false);
  const hasValue = () => {
    const v = local.value;
    return v !== undefined && v !== null && String(v).length > 0;
  };
  const floated = () => focused() || hasValue();

  return (
    <div class={cn('relative', local.class)}>
      <input
        {...rest}
        id={id}
        value={local.value as string | number | undefined}
        inputmode={local.digits ? 'numeric' : local.inputmode}
        onInput={(e) => {
          if (local.digits) keepDigits(e.currentTarget);
          const handler = local.onInput;
          if (typeof handler === 'function') handler(e);
          else if (handler) handler[0](handler[1], e);
        }}
        onFocus={(e) => {
          setFocused(true);
          if (typeof local.onFocus === 'function') local.onFocus(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          if (typeof local.onBlur === 'function') local.onBlur(e);
        }}
        class={cn(
          'peer w-full h-14 pt-5 pb-1 px-4 bg-white/5 text-primary',
          'border rounded-md outline-none',
          'transition-colors duration-base ease-ios',
          local.error ? 'border-danger' : 'border-hairline focus:border-accent',
        )}
      />
      <label
        for={id}
        class={cn(
          'absolute left-4 pointer-events-none transition-all duration-base ease-ios',
          floated() ? 'top-1.5 text-[11px] text-tertiary' : 'top-4 text-[14px] text-secondary',
        )}
      >
        {local.label}
      </label>
      <Show when={local.trailing}>
        <div class="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary">
          {local.trailing}
        </div>
      </Show>
      <Show when={local.error}>
        <p class="mt-1.5 text-[12px] text-danger">{local.error}</p>
      </Show>
    </div>
  );
};

export interface NumberTextFieldProps extends Omit<NumberInputProps, 'placeholder' | 'class'> {
  label: string;
  class?: string;
}

export const NumberTextField: Component<NumberTextFieldProps> = (props) => {
  const [local, rest] = splitProps(props, ['label', 'class', 'id', 'onFocus', 'onBlur']);
  const id = local.id ?? `tf-${++uid}`;
  const [focused, setFocused] = createSignal(false);
  const floated = () => focused() || props.value !== null;

  return (
    <div class={cn('relative', local.class)}>
      <NumberInput
        {...rest}
        id={id}
        onFocus={(e) => {
          setFocused(true);
          if (typeof local.onFocus === 'function') local.onFocus(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          if (typeof local.onBlur === 'function') local.onBlur(e);
        }}
        class={cn(
          'peer w-full h-14 pt-5 pb-1 px-4 bg-white/5 text-primary',
          'border rounded-md outline-none',
          'transition-colors duration-base ease-ios',
          'border-hairline focus:border-accent',
        )}
      />
      <label
        for={id}
        class={cn(
          'absolute left-4 pointer-events-none transition-all duration-base ease-ios',
          floated() ? 'top-1.5 text-[11px] text-tertiary' : 'top-4 text-[14px] text-secondary',
        )}
      >
        {local.label}
      </label>
    </div>
  );
};
